import { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { TimeRange, getUserAdditionStats, UserAdditionStats } from '@/services/analyticsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface UserAdditionsChartProps {
  timeRange: TimeRange;
  standalone?: boolean;
}

const UserAdditionsChart = ({ 
  timeRange,
  standalone = false 
}: UserAdditionsChartProps) => {
  const [data, setData] = useState<UserAdditionStats[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const stats = await getUserAdditionStats(timeRange);
        // Limit to top users for better display based on screen size
        setData(stats.slice(0, isMobile ? 5 : 10));
      } catch (error) {
        console.error('Error fetching user addition stats:', error);
        toast.error('Failed to load user addition statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, isMobile]);

  // Color palette for the bars
  const COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE',
    '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57'
  ];

  const formatUsername = (email: string) => {
    // Return the part before @ if it's an email, otherwise return as is
    if (email.includes('@')) {
      return email.split('@')[0];
    }
    return email;
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
        </div>
      );
    }
    
    if (data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
          No data available for the selected time range
        </div>
      );
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ 
            top: 5, 
            right: isMobile ? 30 : 45, 
            left: isMobile ? 5 : 20, 
            bottom: 5 
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
          <XAxis 
            type="number" 
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickMargin={isMobile ? 5 : 10}
          />
          <YAxis 
            dataKey="user" 
            type="category" 
            width={isMobile ? 80 : 150}
            tickFormatter={formatUsername}
            tick={{ fontSize: isMobile ? 10 : 12 }}
          />
          <Tooltip
            formatter={(value) => [`${value} items`, 'Added']}
            labelFormatter={(label) => `User: ${formatUsername(label)}`}
            contentStyle={{ 
              backgroundColor: 'var(--background)', 
              borderColor: 'var(--border)',
              borderRadius: '0.375rem',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              padding: isMobile ? '8px' : '10px'
            }}
          />
          <Bar 
            dataKey="count" 
            name="Items Added"
            background={{ fill: '#3730a3', opacity: 0.1 }}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
            <LabelList 
              dataKey="count" 
              position="right" 
              style={{ 
                fontSize: isMobile ? 10 : 12,
                fill: 'var(--foreground)'
              }} 
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  if (standalone) {
    return (
      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            User Contributions
          </CardTitle>
          <CardDescription>
            Items added by user for the selected time period
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] sm:h-[300px] w-full">
            {renderChart()}
          </div>
        </CardContent>
      </Card>
    );
  }

  return renderChart();
};

export default UserAdditionsChart;
